import React, { useCallback, useEffect, useRef, useState } from "react";
import { Button } from "../ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../ui/card";
import { Badge } from "../ui/badge";
import { Progress } from "../ui/progress";
import {
  Mic, MicOff, Video as VideoIcon, Camera, Square, RotateCcw,
  CheckCircle, AlertCircle, ArrowRight, ArrowLeft, Sparkles,
  Loader2, Headphones, PlayCircle, Eye,
} from "lucide-react";
import { toast } from "sonner";
import { introsAPI } from "../../services/api";
import BrandedAudioPlayer from "../BrandedAudioPlayer";
import PublishConfirmModal from "./PublishConfirmModal";

// GuidedRecorder — unified video / audio guided recorder built on the new
// intros draft+publish API. Same state machine for both modes; differences
// are gated by the `mode` prop ("video" | "audio").

const VIDEO_MAX_SECONDS = 90;
const VIDEO_SOFT = 60;
const AUDIO_MAX_SECONDS = 25;
const AUDIO_SOFT = 18;
const COUNTDOWN = 3;

const STITCH_STAGES_VIDEO = [
  { key: "preparing_clips", label: "Preparing clips", pct: 0 },
  { key: "trimming_silence", label: "Trimming silence", pct: 5 },
  { key: "normalizing_audio", label: "Normalizing audio", pct: 30 },
  { key: "stitching_interview", label: "Stitching interview", pct: 60 },
  { key: "adding_outro", label: "Adding Inter-View outro", pct: 78 },
  { key: "finalizing_video", label: "Finalizing video", pct: 95 },
];
const STITCH_STAGES_AUDIO = [
  { key: "preparing_clips", label: "Preparing clips", pct: 0 },
  { key: "normalizing_audio", label: "Normalizing audio", pct: 5 },
  { key: "stitching_interview", label: "Stitching introduction", pct: 70 },
  { key: "finalizing_video", label: "Finalizing audio", pct: 95 },
];

const fmtTime = (s) => {
  const m = Math.floor(s / 60); const sec = Math.floor(s % 60);
  return `${m}:${sec.toString().padStart(2, "0")}`;
};
const fmtErr = (err) => {
  const d = err?.response?.data?.detail ?? err?.message ?? err;
  if (!d) return null;
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((i) => (typeof i === "string" ? i : i?.msg || JSON.stringify(i))).join("; ");
  return d?.msg || JSON.stringify(d);
};
const pickMime = (mode) => {
  const list = mode === "audio"
    ? ["audio/webm;codecs=opus", "audio/webm", "audio/ogg;codecs=opus", "audio/mp4"]
    : ["video/webm;codecs=vp9,opus", "video/webm;codecs=vp8,opus", "video/webm", "video/mp4"];
  for (const t of list) {
    if (typeof MediaRecorder !== "undefined" && MediaRecorder.isTypeSupported(t)) return t;
  }
  return "";
};

const GuidedRecorder = ({ mode, questions, onPublished, onCancel }) => {
  const isAudio = mode === "audio";
  const MAX_S = isAudio ? AUDIO_MAX_SECONDS : VIDEO_MAX_SECONDS;
  const SOFT_S = isAudio ? AUDIO_SOFT : VIDEO_SOFT;
  const STAGES = isAudio ? STITCH_STAGES_AUDIO : STITCH_STAGES_VIDEO;

  const [step, setStep] = useState("intro");
  // intro | record | final-review | finalizing | preview | publishing | done | stitch-error
  const [currentIdx, setCurrentIdx] = useState(0);
  const [subState, setSubState] = useState("idle");
  // idle | countdown | recording | review | uploading | save-error

  const [draftIntro, setDraftIntro] = useState(null);
  const [streamReady, setStreamReady] = useState(false);
  const [permError, setPermError] = useState(null);
  const [countdown, setCountdown] = useState(COUNTDOWN);
  const [elapsed, setElapsed] = useState(0);
  const [audioLevel, setAudioLevel] = useState(0);
  const [reviewBlob, setReviewBlob] = useState(null);
  const [reviewURL, setReviewURL] = useState(null);
  const [reviewDuration, setReviewDuration] = useState(0);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [saveErr, setSaveErr] = useState(null);
  const [returnToFinalAfterAccept, setReturnToFinalAfterAccept] = useState(false);
  const [stitchProgress, setStitchProgress] = useState(null);
  const [stitchError, setStitchError] = useState(null);
  const [publishing, setPublishing] = useState(false);
  // Iter-40 publish-confirm modal state. Only shown when there's a
  // PRE-EXISTING published intro for this mode (i.e. publishing would
  // actually replace something live). Brand-new candidates skip it.
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [currentPublished, setCurrentPublished] = useState(null);

  const liveVideoRef = useRef(null);
  const reviewMediaRef = useRef(null);
  const streamRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);
  const startTimeRef = useRef(0);
  const countdownTimerRef = useRef(null);
  const pollTimerRef = useRef(null);
  const audioCtxRef = useRef(null);
  const analyserRef = useRef(null);
  const levelRafRef = useRef(null);

  const stopStream = useCallback(() => {
    if (mediaRecorderRef.current?.state && mediaRecorderRef.current.state !== "inactive") {
      try { mediaRecorderRef.current.stop(); } catch (_) { /* ignore */ }
    }
    mediaRecorderRef.current = null;
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    if (levelRafRef.current) cancelAnimationFrame(levelRafRef.current);
    levelRafRef.current = null;
    if (audioCtxRef.current) {
      try { audioCtxRef.current.close(); } catch (_) { /* ignore */ }
    }
    audioCtxRef.current = null; analyserRef.current = null;
    setStreamReady(false);
  }, []);

  useEffect(() => () => {
    stopStream();
    if (timerRef.current) clearInterval(timerRef.current);
    if (countdownTimerRef.current) clearInterval(countdownTimerRef.current);
    if (pollTimerRef.current) clearInterval(pollTimerRef.current);
    if (reviewURL) URL.revokeObjectURL(reviewURL);
  // eslint-disable-line
  }, []);

  // ── Mount-time short-circuit ──
  // If the recorder mounts while a draft already exists with status
  // ready_to_publish (uploaded intro / derived audio / stitched but
  // unpublished), jump straight to the preview step so the candidate
  // can preview & publish without going back through the recording
  // intro screen.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const st = await introsAPI.getState();
        const d = st[mode]?.draft;
        if (!d || cancelled) return;
        setDraftIntro(d);
        if (d.status === "ready_to_publish") {
          setStep("preview");
        } else if (d.status === "stitch_failed") {
          setStitchError(d.stitch_error || "Stitch failed");
          setStep("stitch-error");
        }
      } catch (e) { /* fall through to intro screen */ }
    })();
    return () => { cancelled = true; };
  }, []);

  // ── Poll while stitching ──
  useEffect(() => {
    if (step !== "finalizing") return undefined;
    let cancelled = false;
    const tick = async () => {
      try {
        const st = await introsAPI.getState();
        const d = st[mode]?.draft;
        if (!d) return;
        setDraftIntro(d);
        setStitchProgress(d.stitch_progress || null);
        if (d.status === "ready_to_publish") {
          if (!cancelled) setStep("preview");
        } else if (d.status === "stitch_failed") {
          if (!cancelled) {
            setStitchError(d.stitch_error || "Stitch failed");
            setStep("stitch-error");
          }
        }
      } catch (e) { /* keep polling */ }
    };
    pollTimerRef.current = setInterval(tick, 3000);
    tick();
    return () => {
      cancelled = true;
      if (pollTimerRef.current) clearInterval(pollTimerRef.current);
      pollTimerRef.current = null;
    };
  }, [step, mode]);

  const requestMedia = async () => {
    setPermError(null);
    try {
      const constraints = isAudio
        ? { audio: { echoCancellation: true, noiseSuppression: true }, video: false }
        : { audio: true, video: { facingMode: "user", width: { ideal: 1280 } } };
      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      streamRef.current = stream;
      if (!isAudio && liveVideoRef.current) {
        liveVideoRef.current.srcObject = stream;
        try { await liveVideoRef.current.play(); } catch (_) { /* ignore */ }
      }
      // Audio level meter for audio mode
      if (isAudio) {
        try {
          const AC = window.AudioContext || window.webkitAudioContext;
          const ctx = new AC();
          const src = ctx.createMediaStreamSource(stream);
          const an = ctx.createAnalyser();
          an.fftSize = 256;
          src.connect(an);
          audioCtxRef.current = ctx; analyserRef.current = an;
          const buf = new Uint8Array(an.frequencyBinCount);
          const loop = () => {
            if (!analyserRef.current) return;
            analyserRef.current.getByteFrequencyData(buf);
            let s = 0;
            for (let i = 0; i < buf.length; i += 1) s += buf[i];
            setAudioLevel(s / buf.length / 255);
            levelRafRef.current = requestAnimationFrame(loop);
          };
          levelRafRef.current = requestAnimationFrame(loop);
        } catch (e) { /* ignore meter */ }
      }
      setStreamReady(true);
    } catch (err) {
      setPermError(err?.message || `${isAudio ? "Microphone" : "Camera"} access denied.`);
    }
  };

  const startCountdown = () => {
    setSubState("countdown"); setCountdown(COUNTDOWN);
    countdownTimerRef.current = setInterval(() => {
      setCountdown((c) => {
        if (c <= 1) {
          clearInterval(countdownTimerRef.current);
          countdownTimerRef.current = null;
          beginRecording();
          return 0;
        }
        return c - 1;
      });
    }, 1000);
  };

  const beginRecording = () => {
    if (!streamRef.current) return;
    const mt = pickMime(mode);
    chunksRef.current = [];
    try {
      const rec = new MediaRecorder(streamRef.current, mt ? { mimeType: mt } : undefined);
      rec.ondataavailable = (e) => { if (e.data?.size > 0) chunksRef.current.push(e.data); };
      rec.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: mt || (isAudio ? "audio/webm" : "video/webm") });
        const url = URL.createObjectURL(blob);
        if (reviewURL) URL.revokeObjectURL(reviewURL);
        setReviewBlob(blob); setReviewURL(url); setReviewDuration(elapsed);
        setSubState("review");
      };
      mediaRecorderRef.current = rec;
      rec.start();
      setSubState("recording"); setElapsed(0);
      startTimeRef.current = Date.now();
      timerRef.current = setInterval(() => {
        const e = (Date.now() - startTimeRef.current) / 1000;
        setElapsed(e);
        if (e >= MAX_S) stopRecording();
      }, 200);
    } catch (err) {
      toast.error("Could not start recording");
      setSubState("idle");
    }
  };

  const stopRecording = () => {
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
    if (mediaRecorderRef.current?.state && mediaRecorderRef.current.state !== "inactive") {
      try { mediaRecorderRef.current.stop(); } catch (_) { /* ignore */ }
    }
  };

  const discardTake = () => {
    if (reviewURL) URL.revokeObjectURL(reviewURL);
    setReviewBlob(null); setReviewURL(null); setReviewDuration(0); setSubState("idle");
  };

  const uploadTake = async () => {
    if (!reviewBlob) return;
    setSubState("uploading"); setUploadProgress(0); setSaveErr(null);
    try {
      await introsAPI.uploadSegment(
        reviewBlob, mode, currentIdx, reviewDuration,
        (p) => setUploadProgress(p), () => {},
      );
      const st = await introsAPI.getState();
      setDraftIntro(st[mode]?.draft || null);
      if (reviewURL) URL.revokeObjectURL(reviewURL);
      setReviewBlob(null); setReviewURL(null); setReviewDuration(0); setUploadProgress(0);

      if (returnToFinalAfterAccept) {
        setReturnToFinalAfterAccept(false); setSubState("idle");
        stopStream(); setStep("final-review"); return;
      }
      const nextIdx = currentIdx + 1;
      if (nextIdx >= questions.length) {
        setSubState("idle"); stopStream(); setStep("final-review");
      } else {
        setCurrentIdx(nextIdx); setSubState("idle");
        toast.success(`Answer ${currentIdx + 1} saved.`);
      }
    } catch (err) {
      setSaveErr(fmtErr(err) || "Save failed.");
      setSubState("save-error");
    }
  };

  const handleStart = async () => {
    try {
      const draft = await introsAPI.createDraft(mode);
      setDraftIntro(draft);
      setStep("record"); setCurrentIdx((draft.segments || []).length);
      setSubState("idle");
      await requestMedia();
    } catch (err) {
      toast.error(fmtErr(err) || "Could not start.");
    }
  };

  const handleReRecordOne = async (idx) => {
    setCurrentIdx(idx); setReturnToFinalAfterAccept(true);
    setStep("record"); setSubState("idle");
    if (!streamRef.current) await requestMedia();
  };

  const handleFinalize = async () => {
    try {
      await introsAPI.finalizeDraft(mode);
      setStep("finalizing"); setStitchError(null);
    } catch (err) {
      toast.error(fmtErr(err) || "Could not finalize.");
    }
  };

  const handleMarkPreviewSeen = async () => {
    if (!draftIntro) return;
    try { await introsAPI.markPreviewSeen(draftIntro.id); } catch (_) { /* ignore */ }
  };

  const handlePublish = async () => {
    if (!draftIntro) return;
    setPublishing(true);
    try {
      await introsAPI.publish(draftIntro.id);
      toast.success(`${isAudio ? "Audio" : "Video"} introduction published.`);
      setStep("done");
      setConfirmOpen(false);
      if (onPublished) onPublished();
    } catch (err) {
      toast.error(fmtErr(err) || "Could not publish.");
    } finally {
      setPublishing(false);
    }
  };

  // Iter-40 — fetch the current published intro (if any) for this
  // mode, and open the confirm modal only when it would actually
  // replace something live. First-time publishers skip the modal so
  // we don't add friction to a frictionless flow.
  const handlePublishClick = async () => {
    if (!draftIntro) return;
    await handleMarkPreviewSeen();
    try {
      const state = await introsAPI.getState();
      const current = state?.[mode]?.published || null;
      if (current && (current.media_url || current.playback_url)) {
        setCurrentPublished(current);
        setConfirmOpen(true);
        return;
      }
    } catch (err) {
      // If the lookup fails, fall through to publish — we'd rather
      // not block the candidate over a momentary network blip.
      console.warn("could not fetch current published intro for confirm modal", err);
    }
    handlePublish();
  };

  const handleDiscard = async () => {
    if (!window.confirm("Discard this draft? Your published introduction stays live.")) return;
    try { await introsAPI.discardDraft(mode); } catch (_) { /* ignore */ }
    if (onCancel) onCancel();
  };

  // ─── Render ───
  if (step === "intro") {
    return (
      <Card data-testid={`recorder-${mode}-intro`} className="border-blue-100">
        <CardHeader>
          <div className="flex items-center gap-2">
            {isAudio ? <Headphones className="h-5 w-5 text-blue-600" /> : <VideoIcon className="h-5 w-5 text-blue-600" />}
            <CardTitle>Record a new {isAudio ? "audio" : "video"} introduction</CardTitle>
          </div>
          <CardDescription>
            {isAudio
              ? `5 quick voice answers, up to ${MAX_S} seconds each. We'll blend them into a single MP3.`
              : `5 short video answers, up to ${MAX_S} seconds each. We'll stitch them into one polished MP4.`}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="rounded-xl border border-blue-100 bg-blue-50/60 p-4 text-sm text-slate-700">
            <p className="font-semibold mb-1 flex items-center gap-1.5"><Sparkles className="h-4 w-4 text-blue-600" /> Draft mode</p>
            Your currently-published introduction stays live the whole time. Nothing changes for recruiters until you click <span className="font-semibold">Publish</span>.
          </div>
          <div className="flex flex-wrap gap-3">
            <Button onClick={handleStart} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid={`recorder-${mode}-start-btn`}>
              {isAudio ? <Mic className="h-4 w-4 mr-2" /> : <Camera className="h-4 w-4 mr-2" />}
              Start recording
            </Button>
            <Button
              variant="outline"
              onClick={onCancel}
              data-testid={`recorder-${mode}-back-btn`}
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to introductions
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (step === "record") {
    const question = questions[currentIdx] || `Question ${currentIdx + 1}`;
    const overSoft = elapsed >= SOFT_S;
    return (
      <Card data-testid={`recorder-${mode}-record`}>
        <CardHeader className="sticky top-0 bg-white z-10 border-b">
          <div className="flex items-center justify-between">
            <Badge variant="outline" className="border-blue-200 text-blue-700">Q{currentIdx + 1} of {questions.length}</Badge>
            {subState === "recording" && (
              <span className={`text-sm font-mono ${overSoft ? "text-amber-600 font-semibold" : "text-slate-600"}`}>
                {fmtTime(elapsed)} / {fmtTime(MAX_S)}
              </span>
            )}
          </div>
          <CardTitle className="text-xl mt-3">{question}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-6 py-6">
          {permError && (
            <div className="flex items-start gap-3 rounded-lg border border-rose-200 bg-rose-50 p-4 text-rose-700 text-sm">
              {isAudio ? <MicOff className="h-5 w-5 mt-0.5" /> : <AlertCircle className="h-5 w-5 mt-0.5" />}
              <div className="flex-1">
                <p className="font-semibold">{isAudio ? "Microphone" : "Camera"} unavailable</p>
                <p>{permError}</p>
                <Button size="sm" variant="outline" onClick={requestMedia} className="mt-3">Try again</Button>
              </div>
            </div>
          )}

          {!permError && !isAudio && (
            <div className="rounded-2xl overflow-hidden bg-slate-900 aspect-video">
              <video ref={liveVideoRef} muted playsInline className="w-full h-full object-cover" />
            </div>
          )}

          {!permError && isAudio && (
            <div className="relative rounded-2xl border border-slate-200 bg-gradient-to-br from-slate-900 to-slate-700 text-white p-8 sm:p-12 overflow-hidden">
              <div className="flex flex-col items-center text-center space-y-4">
                <div
                  className={`h-20 w-20 rounded-full flex items-center justify-center transition-all duration-200 ${
                    subState === "recording" ? "bg-rose-500/90 ring-8 ring-rose-500/30" : "bg-blue-500/80 ring-4 ring-blue-500/20"
                  }`}
                  style={{ transform: subState === "recording" ? `scale(${1 + Math.min(0.25, audioLevel * 0.8)})` : "scale(1)" }}
                >
                  <Mic className="h-10 w-10" />
                </div>
                <p className="text-sm uppercase tracking-[0.18em] text-blue-200/80 font-semibold">
                  {subState === "countdown" ? "Get ready…" : subState === "recording" ? "Recording" : streamReady ? "Microphone ready" : "Requesting microphone…"}
                </p>
                {subState === "countdown" && (
                  <p className="text-7xl font-bold tabular-nums">{countdown}</p>
                )}
                {subState === "recording" && (
                  <div className="w-full max-w-md">
                    <div className="h-2 bg-white/10 rounded-full overflow-hidden">
                      <div className="h-full bg-gradient-to-r from-emerald-400 to-rose-400" style={{ width: `${Math.min(100, audioLevel * 220)}%` }} />
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {subState === "countdown" && !isAudio && (
            <div className="text-center text-6xl font-bold text-blue-600 tabular-nums">{countdown}</div>
          )}

          {!permError && subState === "idle" && streamReady && (
            <div className="flex flex-wrap gap-3 justify-center">
              <Button onClick={startCountdown} className="bg-rose-600 hover:bg-rose-700 text-white" data-testid={`recorder-${mode}-record-start-btn`}>
                {isAudio ? <Mic className="h-4 w-4 mr-2" /> : <Camera className="h-4 w-4 mr-2" />}
                Start recording answer
              </Button>
            </div>
          )}

          {subState === "recording" && !isAudio && (
            <div className="flex justify-center">
              <Button onClick={stopRecording} className="bg-slate-900 hover:bg-slate-800 text-white">
                <Square className="h-4 w-4 mr-2" /> Stop recording
              </Button>
            </div>
          )}
          {subState === "recording" && isAudio && (
            <div className="flex justify-center">
              <Button onClick={stopRecording} className="bg-slate-900 hover:bg-slate-800 text-white">
                <Square className="h-4 w-4 mr-2" /> Stop recording
              </Button>
            </div>
          )}

          {subState === "review" && reviewURL && (
            <div className="space-y-4">
              {isAudio
                ? <audio ref={reviewMediaRef} src={reviewURL} controls className="w-full" />
                : <video ref={reviewMediaRef} src={reviewURL} controls playsInline className="w-full rounded-lg bg-slate-900" />}
              <p className="text-sm text-slate-600 text-center">{fmtTime(reviewDuration)} captured</p>
              <div className="flex flex-wrap gap-3 justify-center">
                <Button variant="outline" onClick={discardTake}><RotateCcw className="h-4 w-4 mr-2" />Re-record</Button>
                <Button onClick={uploadTake} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid={`recorder-${mode}-accept-btn`}>
                  <CheckCircle className="h-4 w-4 mr-2" /> Use this take
                </Button>
              </div>
            </div>
          )}

          {subState === "uploading" && (
            <div className="space-y-3">
              <Progress value={uploadProgress} className="h-2" />
              <p className="text-center text-sm text-slate-600">Saving… {uploadProgress}%</p>
            </div>
          )}

          {subState === "save-error" && (
            <div className="rounded-lg border border-rose-200 bg-rose-50 p-4 text-rose-800 text-sm space-y-3">
              <p className="font-semibold flex items-center gap-2"><AlertCircle className="h-5 w-5" /> Couldn't save.</p>
              <p>{saveErr}</p>
              <div className="flex gap-3">
                <Button size="sm" onClick={uploadTake} className="bg-rose-600 hover:bg-rose-700 text-white">Try again</Button>
                <Button size="sm" variant="outline" onClick={discardTake}>Discard</Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    );
  }

  if (step === "final-review") {
    return (
      <Card data-testid={`recorder-${mode}-final-review`}>
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><CheckCircle className="h-5 w-5 text-emerald-600" />Review your 5 answers</CardTitle>
          <CardDescription>
            Listen/watch each take. Re-record any answer, or stitch them into your final {isAudio ? "audio introduction" : "interview"}.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-5">
          {(draftIntro?.segments || []).map((seg) => (
            <div key={seg.index} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
              <div className="text-xs uppercase tracking-[0.18em] text-blue-600 font-semibold mb-1">Question {seg.index + 1}</div>
              <p className="text-sm text-slate-800 font-medium">{seg.question}</p>
              {seg.playback_url && (isAudio
                ? <audio src={seg.playback_url} controls className="w-full mt-3" />
                : <video src={seg.playback_url} controls playsInline className="w-full mt-3 rounded-lg bg-slate-900" />)}
              <div className="mt-3">
                <Button size="sm" variant="outline" onClick={() => handleReRecordOne(seg.index)} data-testid={`recorder-${mode}-redo-${seg.index}`}>
                  <RotateCcw className="h-4 w-4 mr-2" /> Re-record this answer
                </Button>
              </div>
            </div>
          ))}
          <div className="flex flex-wrap gap-3 justify-between pt-2">
            <Button variant="outline" onClick={handleDiscard}><ArrowLeft className="h-4 w-4 mr-2" />Discard draft</Button>
            <Button onClick={handleFinalize} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid={`recorder-${mode}-finalize-btn`}>
              <Sparkles className="h-4 w-4 mr-2" />Stitch into final {isAudio ? "audio" : "video"}<ArrowRight className="h-4 w-4 ml-2" />
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (step === "finalizing") {
    const pct = stitchProgress?.pct ?? 5;
    const currentStage = stitchProgress?.stage || STAGES[0].key;
    return (
      <Card data-testid={`recorder-${mode}-finalizing`}>
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><Loader2 className="h-5 w-5 animate-spin text-blue-600" />Stitching</CardTitle>
        </CardHeader>
        <CardContent className="space-y-5">
          <Progress value={pct} className="h-2" />
          <p className="text-xs text-slate-500">{Math.round(pct)}%</p>
          <ul className="space-y-2">
            {STAGES.map((s) => {
              const reached = pct >= s.pct; const active = s.key === currentStage;
              return (
                <li key={s.key} className={`flex items-center gap-2 text-sm ${active ? "text-blue-700 font-semibold" : reached ? "text-emerald-700" : "text-slate-400"}`}>
                  {reached && !active ? <CheckCircle className="h-4 w-4" /> : active ? <Loader2 className="h-4 w-4 animate-spin" /> : <PlayCircle className="h-4 w-4" />}
                  {s.label}
                </li>
              );
            })}
          </ul>
        </CardContent>
      </Card>
    );
  }

  if (step === "preview") {
    return (
      <Card data-testid={`recorder-${mode}-preview`} className="border-blue-200">
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><Eye className="h-5 w-5 text-blue-600" />Preview before publishing</CardTitle>
          <CardDescription>
            Watch/listen to the final stitched output. Click <span className="font-semibold">Publish</span> when you're ready to make it live.
            Your previously-published introduction stays live until you confirm.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-5">
          {isAudio
            ? <BrandedAudioPlayer src={draftIntro?.playback_url} durationSeconds={draftIntro?.duration_seconds} data-testid={`recorder-${mode}-preview-player`} />
            : <video src={draftIntro?.playback_url} controls playsInline preload="metadata" className="w-full aspect-video bg-black rounded-2xl" data-testid={`recorder-${mode}-preview-player`} onPlay={handleMarkPreviewSeen} />}
          <div className="flex flex-wrap gap-3 justify-between">
            <Button variant="outline" onClick={handleDiscard}><ArrowLeft className="h-4 w-4 mr-2" />Discard & re-record</Button>
            <Button
              onClick={handlePublishClick}
              disabled={publishing}
              className="bg-blue-600 hover:bg-blue-700 text-white"
              data-testid={`recorder-${mode}-publish-btn`}
            >
              {publishing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <CheckCircle className="h-4 w-4 mr-2" />}
              Publish {isAudio ? "audio" : "video"} introduction
            </Button>
          </div>
        </CardContent>
        <PublishConfirmModal
          open={confirmOpen}
          mode={mode}
          currentPublished={currentPublished}
          newDraft={draftIntro}
          publishing={publishing}
          onCancel={() => setConfirmOpen(false)}
          onConfirm={handlePublish}
        />
      </Card>
    );
  }

  if (step === "stitch-error") {
    return (
      <Card data-testid={`recorder-${mode}-stitch-error`} className="border-rose-200">
        <CardHeader>
          <CardTitle className="text-rose-700 flex items-center gap-2"><AlertCircle className="h-5 w-5" />Couldn't stitch</CardTitle>
          <CardDescription>{stitchError}</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex gap-3">
            <Button onClick={handleFinalize} className="bg-rose-600 hover:bg-rose-700 text-white">Retry</Button>
            <Button variant="outline" onClick={() => setStep("final-review")}>Back to review</Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  // done
  return (
    <Card data-testid={`recorder-${mode}-done`} className="border-emerald-200">
      <CardHeader>
        <CardTitle className="text-emerald-700 flex items-center gap-2"><CheckCircle className="h-5 w-5" />Published</CardTitle>
        <CardDescription>Your {isAudio ? "audio" : "video"} introduction is live.</CardDescription>
      </CardHeader>
      <CardContent>
        <Button variant="outline" onClick={onCancel}>Back to introductions</Button>
      </CardContent>
    </Card>
  );
};

export default GuidedRecorder;
